<script setup lang="ts">
import {
  instanceOfBeamShapingGroupCircularDto,
  instanceOfBeamShapingGroupRectangularDto,
  instanceOfDiscreteNumericValueDto,
  instanceOfElectronBeamQualityCombinationValueDto,
  instanceOfEnumerationValueDto,
  instanceOfNumericValueDto,
  instanceOfPhotonBeamQualityCombinationValueDto,
  type ISystemSetupCombinationValue,
} from '@/api/generator';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils';
import { translateEntityProperty } from '@/utils/helper';
import { getFormattingOptionsFromNumericValue, toDisplayValue } from '@/utils/numberUtils';
import { isNumber } from 'lodash';

defineProps<{
  item: ISystemSetupCombinationValue;
}>();
</script>

<template>
  <template
    v-if="
      instanceOfElectronBeamQualityCombinationValueDto(item) ||
      instanceOfPhotonBeamQualityCombinationValueDto(item)
    "
  >
    <span style="min-width: 5em">{{ item.beamQualityName }}</span>
    |
    <span v-if="item.selectedDoseRate" style="min-width: 7em">
      {{
        toDisplayValue(
          item.selectedDoseRate.value,
          getFormattingOptionsFromNumericValue(item.selectedDoseRate),
        )
      }}
    </span>
    <span v-else style="min-width: 7em">
      {{ EMPTY_FIELD_SIGN }}
    </span>
  </template>

  <template v-else-if="instanceOfBeamShapingGroupRectangularDto(item)">
    <span style="min-width: 8em">
      {{
        toDisplayValue(
          item.radiationFieldSizeX?.value,
          getFormattingOptionsFromNumericValue(item.radiationFieldSizeX!),
        )
      }}
      |
      {{
        toDisplayValue(
          item.radiationFieldSizeY?.value,
          getFormattingOptionsFromNumericValue(item.radiationFieldSizeY!),
        )
      }}
    </span>
  </template>

  <template v-else-if="instanceOfBeamShapingGroupCircularDto(item)">
    <span style="min-width: 8em">
      &#8960;
      {{
        toDisplayValue(
          item.radiationFieldSize?.value,
          getFormattingOptionsFromNumericValue(item.radiationFieldSize!),
        )
      }}
    </span>
  </template>

  <template v-else-if="instanceOfNumericValueDto(item) && isNumber(item.value)">
    <span style="min-width: 4em">
      {{ toDisplayValue(item.value, getFormattingOptionsFromNumericValue(item)) }}
    </span>
  </template>

  <template v-else-if="instanceOfEnumerationValueDto(item) && item.value?.name">
    <span style="min-width: 7em">
      {{ translateEntityProperty('name', item.value) }}
    </span>
  </template>

  <template v-else-if="instanceOfDiscreteNumericValueDto(item) && isNumber(item.value)">
    <span style="min-width: 7em">
      {{ toDisplayValue(item.value, getFormattingOptionsFromNumericValue(item)) }}
    </span>
  </template>

  <template v-else>
    {{ EMPTY_FIELD_SIGN }}
  </template>
</template>

<style scoped lang="scss">
.no-line-break {
  white-space: nowrap;
}
</style>
